import {
  TBodyParamsPage,
  TEventDatePage,
  TInfoPage,
  TInputPage,
  TPageConfig,
  TSelectionPage,
} from '@/config/types'
import { PageName } from '@/enums/pageName'
import { downloadImage } from '@/utils/imageUtils'
import { getTranslation } from '@/utils/localisationUtils'
import { getPath } from '@/utils/urlUtils'
import { getBMI } from '@/utils/weightUtils'

import {
  avocado,
  babyBottle,
  bed,
  biceps,
  bride,
  broccoli,
  cake,
  chocolate,
  coolHand,
  dejected,
  doubleExclamationMark,
  doughnut,
  exhale,
  facepalming,
  fire,
  firecracker,
  fish,
  friedEgg,
  frowning,
  grimacing,
  happy,
  heat,
  hospital,
  island,
  lotusPosition,
  meat,
  meatOnBone,
  money,
  oldMan,
  rocket,
  rollingEyes,
  salt,
  seedling,
  sleepingFace,
  smile,
  soda,
  sportCup,
  stetoskop,
  stress,
  sun,
  sunglassesFace,
  sushi,
  thinkingFace,
  waxingCrescentMoon,
  woman,
  womanCrossArms,
  womanRaisedHand,
  womanRelax,
  womanTypingHand,
  x,
  yawning,
  zzz,
} from '@/shared/emoji'
import { CalendarSquare } from '@/shared/icons/CalendarSquare'
import { RectRoundCheckbox4 } from '@/shared/icons/RectRoundCheckbox4'
import { WomanCrosshanded } from '@/shared/icons/WomanСrosshanded'
import breakfast_5 from '@/shared/images/breakfast-5.png'
import breakfast_6 from '@/shared/images/breakfast-6.png'
import breakfast_7 from '@/shared/images/breakfast-7.png'
import breakfast_8 from '@/shared/images/breakfast-8.png'
import dinner_5 from '@/shared/images/dinner-5.png'
import dinner_6 from '@/shared/images/dinner-6.png'
import dinner_7 from '@/shared/images/dinner-7.png'
import glassWater from '@/shared/images/glass-water.png'
import lunch_5 from '@/shared/images/lunch-5.png'
import lunch_6 from '@/shared/images/lunch-6.png'
import lunch_7 from '@/shared/images/lunch-7.png'
import {
  age18_20,
  age36_45,
  age46_55,
  age55,
  backgroundWoman_1,
  backgroundWoman_2,
  backgroundWoman_3,
  backgroundWoman_4,
  backPain,
  belly,
  butt,
  chest,
  curvy,
  danceWomans,
  heavierSide,
  kneesPain,
  legs,
  midSized,
  nonePain,
  overweight,
  slim,
  toned,
} from '@/shared/images/organic-fit'

import { contactNameKey } from '@/core/constants'

import i18n from './../../i18n'
import { getItem, setItem } from '../storage'

const PAGE_TOTAL_COUNT = 29
const bmi = (value: string, measurementSystem: string) =>
  Number(getBMI(getItem, value, measurementSystem)?.toFixed(2))

export const pagesConfig: TPageConfig<
  TInfoPage | TBodyParamsPage | TInputPage | TEventDatePage | TSelectionPage
> = {
  [PageName.age]: {
    title: i18n.t(`age.title`),
    pageDescription: i18n.t(`age.pageDescription`),
    nextUrl: getPath(PageName.audience),
    options: [
      { title: i18n.t(`age.options.0.title`), image: downloadImage(age18_20) },
      { title: i18n.t(`age.options.1.title`), image: downloadImage(age36_45) },
      { title: i18n.t(`age.options.2.title`), image: downloadImage(age46_55) },
      { title: i18n.t(`age.options.3.title`), image: downloadImage(age55) },
    ],
    policyBlock: true,
  },
  [PageName.audience]: {
    title: i18n.t(`audience.title`),
    pageDescription: i18n.t(`audience.pageDescription`),
    nextUrl: getPath(PageName.mainGoal),
    infoImage: downloadImage(danceWomans),
    buttonName: i18n.t(`audience.buttonName`),
    policyBlock: true,
  },
  [PageName.mainGoal]: {
    title: i18n.t(`mainGoal.title`),
    nextUrl: getPath(PageName.physicalBuild),
    pageNumber: 1,
    progress: ((1 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`mainGoal.options.0.title`), image: downloadImage(biceps) },
      { title: i18n.t(`mainGoal.options.1.title`), image: downloadImage(fire) },
      {
        title: i18n.t(`mainGoal.options.2.title`),
        image: downloadImage(lotusPosition),
      },
      {
        title: i18n.t(`mainGoal.options.3.title`),
        image: downloadImage(womanRelax),
      },
      { title: i18n.t(`mainGoal.options.4.title`), image: downloadImage(woman) },
    ],
    policyBlock: true,
  },
  [PageName.physicalBuild]: {
    title: i18n.t(`physicalBuild.title`),
    tabTitle: i18n.t(`physicalBuild.tabTitle`),
    nextUrl: getPath(PageName.dreamBody),
    pageNumber: 2,
    progress: ((2 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`physicalBuild.options.0.title`), image: downloadImage(slim) },
      {
        title: i18n.t(`physicalBuild.options.1.title`),
        image: downloadImage(midSized),
      },
      {
        title: i18n.t(`physicalBuild.options.2.title`),
        image: downloadImage(heavierSide),
      },
      {
        title: i18n.t(`physicalBuild.options.3.title`),
        image: downloadImage(overweight),
      },
    ],
    policyBlock: true,
  },
  [PageName.dreamBody]: {
    title: i18n.t(`dreamBody.title`),
    tabTitle: i18n.t(`dreamBody.tabTitle`),
    nextUrl: getPath(PageName.areasToFocus),
    pageNumber: 3,
    progress: ((3 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`dreamBody.options.0.title`), image: downloadImage(slim) },
      { title: i18n.t(`dreamBody.options.1.title`), image: downloadImage(toned) },
      { title: i18n.t(`dreamBody.options.2.title`), image: downloadImage(curvy) },
      { title: i18n.t(`dreamBody.options.3.title`), image: downloadImage(midSized) },
    ],
    policyBlock: true,
  },
  [PageName.areasToFocus]: {
    title: i18n.t(`areasToFocus.title`),
    nextUrl: getPath(PageName.trainingExperience),
    pageNumber: 4,
    progress: ((4 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    buttonName: i18n.t(`areasToFocus.buttonName`),
    options: [
      { title: i18n.t(`areasToFocus.options.0.title`), image: downloadImage(belly) },
      { title: i18n.t(`areasToFocus.options.1.title`), image: downloadImage(butt) },
      { title: i18n.t(`areasToFocus.options.2.title`), image: downloadImage(chest) },
      { title: i18n.t(`areasToFocus.options.3.title`), image: downloadImage(legs) },
    ],
    customCheckbox: <RectRoundCheckbox4 />,
    showDefaultCircle: true,
    policyBlock: true,
  },

  [PageName.trainingExperience]: {
    title: i18n.t(`trainingExperience.title`),
    nextUrl: getPath(PageName.lastIdealWeigh),
    pageNumber: 5,
    progress: ((5 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`trainingExperience.options.0.title`), image: downloadImage(rollingEyes) },
      { title: i18n.t(`trainingExperience.options.1.title`), image: downloadImage(biceps) },
      { title: i18n.t(`trainingExperience.options.2.title`), image: downloadImage(grimacing) },
    ],
    policyBlock: true,
  },
  [PageName.lastIdealWeigh]: {
    title: i18n.t(`lastIdealWeigh.title`),
    nextUrl: getPath(PageName.pilatesExperience),
    pageNumber: 6,
    progress: ((6 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`lastIdealWeigh.options.0.title`), image: downloadImage(thinkingFace) },
      { title: i18n.t(`lastIdealWeigh.options.1.title`), image: downloadImage(frowning) },
      { title: i18n.t(`lastIdealWeigh.options.2.title`), image: downloadImage(facepalming) },
      { title: i18n.t(`lastIdealWeigh.options.3.title`), image: downloadImage(x) },
    ],
    policyBlock: true,
  },
  [PageName.pilatesExperience]: {
    title: i18n.t(`pilatesExperience.title`),
    nextUrl: getPath(PageName.flexibility),
    pageNumber: 7,
    progress: ((7 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`pilatesExperience.options.0.title`), image: downloadImage(thinkingFace) },
      { title: i18n.t(`pilatesExperience.options.1.title`), image: downloadImage(frowning) },
      { title: i18n.t(`pilatesExperience.options.2.title`), image: downloadImage(facepalming) },
    ],
    policyBlock: true,
  },

  [PageName.flexibility]: {
    title: i18n.t(`flexibility.title`),
    nextUrl: getPath(PageName.workout),
    pageNumber: 8,
    progress: ((8 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    backgroundImage: downloadImage(backgroundWoman_1),
    options: [
      { title: i18n.t(`flexibility.options.0.title`) },
      { title: i18n.t(`flexibility.options.1.title`) },
      { title: i18n.t(`flexibility.options.2.title`) },
      { title: i18n.t(`flexibility.options.3.title`) },
    ],
    policyBlock: true,
  },
  [PageName.workout]: {
    title: i18n.t(`workout.title`),
    nextUrl: getPath(PageName.shortnessOfBreath),
    pageNumber: 9,
    progress: ((9 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    backgroundImage: downloadImage(backgroundWoman_2),
    options: [
      { title: i18n.t(`workout.options.0.title`) },
      { title: i18n.t(`workout.options.1.title`) },
      { title: i18n.t(`workout.options.2.title`) },
      { title: i18n.t(`workout.options.3.title`) },
    ],
    policyBlock: true,
  },
  [PageName.shortnessOfBreath]: {
    title: i18n.t(`shortnessOfBreath.title`),
    nextUrl: getPath(PageName.walkingFrequency),
    pageNumber: 10,
    progress: ((10 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`shortnessOfBreath.options.0.title`), image: downloadImage(heat) },
      { title: i18n.t(`shortnessOfBreath.options.1.title`), image: downloadImage(exhale) },
      { title: i18n.t(`shortnessOfBreath.options.2.title`), image: downloadImage(happy) },
      { title: i18n.t(`shortnessOfBreath.options.3.title`), image: downloadImage(rocket) },
    ],
    policyBlock: true,
  },
  [PageName.walkingFrequency]: {
    title: i18n.t(`walkingFrequency.title`),
    nextUrl: getPath(PageName.struggle),
    pageNumber: 11,
    progress: ((11 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      {
        title: i18n.t(`walkingFrequency.options.0.title`),
        image: downloadImage(sunglassesFace),
      },
      { title: i18n.t(`walkingFrequency.options.1.title`), image: downloadImage(biceps) },
      { title: i18n.t(`walkingFrequency.options.2.title`), image: downloadImage(happy) },
      { title: i18n.t(`walkingFrequency.options.3.title`), image: downloadImage(coolHand) },
    ],
    policyBlock: true,
  },
  [PageName.struggle]: {
    title: i18n.t(`struggle.title`),
    nextUrl: getPath(PageName.workSchedule),
    pageNumber: 12,
    progress: ((12 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`struggle.options.0.title`), image: downloadImage(backPain) },
      { title: i18n.t(`struggle.options.1.title`), image: downloadImage(kneesPain) },
      { title: i18n.t(`struggle.options.2.title`), image: downloadImage(nonePain) },
    ],
    customCheckbox: <RectRoundCheckbox4 />,
    showDefaultCircle: true,
    policyBlock: true,
    buttonName: i18n.t(`struggle.buttonName`),
  },
  [PageName.workSchedule]: {
    title: i18n.t(`workSchedule.title`),
    nextUrl: getPath(PageName.describeYourDay),
    pageNumber: 13,
    progress: ((13 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`workSchedule.options.0.title`), image: downloadImage(sun) },
      {
        title: i18n.t(`workSchedule.options.1.title`),
        image: downloadImage(waxingCrescentMoon),
      },
      { title: i18n.t(`workSchedule.options.2.title`), image: downloadImage(womanRaisedHand) },
      { title: i18n.t(`workSchedule.options.3.title`), image: downloadImage(oldMan) },
    ],
    policyBlock: true,
  },
  [PageName.describeYourDay]: {
    title: i18n.t(`describeYourDay.title`),
    nextUrl: getPath(PageName.energyLevels),
    pageNumber: 14,
    progress: ((14 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`describeYourDay.options.0.title`), image: downloadImage(sun) },
      {
        title: i18n.t(`describeYourDay.options.1.title`),
        image: downloadImage(waxingCrescentMoon),
      },
      {
        title: i18n.t(`describeYourDay.options.2.title`),
        image: downloadImage(womanRaisedHand),
      },
    ],
    policyBlock: true,
  },
  [PageName.energyLevels]: {
    title: i18n.t(`energyLevels.title`),
    nextUrl: getPath(PageName.sleepRoutine),
    pageNumber: 15,
    progress: ((15 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    backgroundImage: downloadImage(backgroundWoman_3),
    options: [
      { title: i18n.t(`energyLevels.options.0.title`) },
      { title: i18n.t(`energyLevels.options.1.title`) },
      { title: i18n.t(`energyLevels.options.2.title`) },
      { title: i18n.t(`energyLevels.options.3.title`) },
    ],
    policyBlock: true,
  },
  [PageName.sleepRoutine]: {
    title: i18n.t(`sleepRoutine.title`),
    nextUrl: getPath(PageName.waterIntake),
    pageNumber: 16,
    progress: ((16 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`sleepRoutine.options.0.title`), image: downloadImage(yawning) },
      { title: i18n.t(`sleepRoutine.options.1.title`), image: downloadImage(sleepingFace) },
      { title: i18n.t(`sleepRoutine.options.2.title`), image: downloadImage(bed) },
      { title: i18n.t(`sleepRoutine.options.3.title`), image: downloadImage(zzz) },
    ],
    policyBlock: true,
  },
  [PageName.waterIntake]: {
    title: i18n.t(`waterIntake.title`),
    nextUrl: getPath(PageName.breakfast),
    pageNumber: 17,
    progress: ((17 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    backgroundImage: downloadImage(backgroundWoman_4),
    options: [
      { title: i18n.t(`waterIntake.options.0.title`) },
      { title: i18n.t(`waterIntake.options.1.title`) },
      { title: i18n.t(`waterIntake.options.2.title`) },
      { title: i18n.t(`waterIntake.options.3.title`) },
    ],
    policyBlock: true,
  },
  [PageName.breakfast]: {
    title: i18n.t(`breakfast.title`),
    nextUrl: getPath(PageName.lunch),
    pageNumber: 18,
    progress: ((18 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`breakfast.options.0.title`), image: downloadImage(breakfast_5) },
      { title: i18n.t(`breakfast.options.1.title`), image: downloadImage(breakfast_6) },
      { title: i18n.t(`breakfast.options.2.title`), image: downloadImage(breakfast_7) },
      { title: i18n.t(`breakfast.options.3.title`), image: downloadImage(breakfast_8) },
    ],
    policyBlock: true,
  },
  [PageName.lunch]: {
    title: i18n.t(`lunch.title`),
    nextUrl: getPath(PageName.dinner),
    pageNumber: 19,
    progress: ((19 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`lunch.options.0.title`), image: downloadImage(lunch_5) },
      { title: i18n.t(`lunch.options.1.title`), image: downloadImage(lunch_6) },
      { title: i18n.t(`lunch.options.2.title`), image: downloadImage(lunch_7) },
      { title: i18n.t(`lunch.options.3.title`), image: downloadImage(glassWater) },
    ],
    policyBlock: true,
  },
  [PageName.dinner]: {
    title: i18n.t(`dinner.title`),
    nextUrl: getPath(PageName.diet),
    pageNumber: 20,
    progress: ((20 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`dinner.options.0.title`), image: downloadImage(dinner_5) },
      { title: i18n.t(`dinner.options.1.title`), image: downloadImage(dinner_6) },
      { title: i18n.t(`dinner.options.2.title`), image: downloadImage(dinner_7) },
      { title: i18n.t(`dinner.options.3.title`), image: downloadImage(glassWater) },
    ],
    policyBlock: true,
  },
  [PageName.diet]: {
    title: i18n.t(`diet.title`),
    nextUrl: getPath(PageName.badHabits),
    pageNumber: 21,
    progress: ((21 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      {
        title: i18n.t(`diet.options.0.title`),
        description: i18n.t(`diet.options.0.description`),
        image: downloadImage(friedEgg),
      },
      {
        title: i18n.t(`diet.options.1.title`),
        description: i18n.t(`diet.options.1.description`),
        image: downloadImage(meat),
      },
      {
        title: i18n.t(`diet.options.2.title`),
        description: i18n.t(`diet.options.2.description`),
        image: downloadImage(seedling),
      },
      {
        title: i18n.t(`diet.options.3.title`),
        description: i18n.t(`diet.options.3.description`),
        image: downloadImage(broccoli),
      },
      {
        title: i18n.t(`diet.options.4.title`),
        description: i18n.t(`diet.options.4.description`),
        image: downloadImage(avocado),
      },
      {
        title: i18n.t(`diet.options.5.title`),
        description: i18n.t(`diet.options.5.description`),
        image: downloadImage(sushi),
      },
      {
        title: i18n.t(`diet.options.6.title`),
        description: i18n.t(`diet.options.6.description`),
        image: downloadImage(babyBottle),
      },
      {
        title: i18n.t(`diet.options.7.title`),
        description: i18n.t(`diet.options.7.description`),
        image: downloadImage(doughnut),
      },
      {
        title: i18n.t(`diet.options.8.title`),
        description: i18n.t(`diet.options.8.description`),
        image: downloadImage(meatOnBone),
      },
      {
        title: i18n.t(`diet.options.9.title`),
        description: i18n.t(`diet.options.9.description`),
        image: downloadImage(fish),
      },
    ],
    policyBlock: true,
  },
  [PageName.badHabits]: {
    title: i18n.t(`badHabits.title`),
    pageDescription: i18n.t(`badHabits.pageDescription`),
    nextUrl: getPath(PageName.weightOnEvents),
    pageNumber: 22,
    progress: ((22 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`badHabits.options.0.title`), image: downloadImage(zzz) },
      { title: i18n.t(`badHabits.options.1.title`), image: downloadImage(chocolate) },
      { title: i18n.t(`badHabits.options.2.title`), image: downloadImage(soda) },
      { title: i18n.t(`badHabits.options.3.title`), image: downloadImage(salt) },
      { title: i18n.t(`badHabits.options.4.title`), image: downloadImage(x) },
    ],
    buttonName: i18n.t(`badHabits.buttonName`),
    customCheckbox: <RectRoundCheckbox4 />,
    showDefaultCircle: true,
    policyBlock: true,
  },
  [PageName.weightOnEvents]: {
    title: i18n.t(`weightOnEvents.title`),
    pageDescription: i18n.t(`weightOnEvents.pageDescription`),
    nextUrl: getPath(PageName.height),
    pageNumber: 23,
    progress: ((23 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t(`weightOnEvents.options.0.title`), image: downloadImage(bride) },
      { title: i18n.t(`weightOnEvents.options.1.title`), image: downloadImage(dejected) },
      { title: i18n.t(`weightOnEvents.options.2.title`), image: downloadImage(money) },
      { title: i18n.t(`weightOnEvents.options.3.title`), image: downloadImage(hospital) },
      { title: i18n.t(`weightOnEvents.options.4.title`), image: downloadImage(stress) },
      { title: i18n.t(`weightOnEvents.options.5.title`), image: downloadImage(oldMan) },
      { title: i18n.t(`weightOnEvents.options.6.title`), image: downloadImage(stetoskop) },
      { title: i18n.t(`weightOnEvents.options.7.title`), image: downloadImage(x) },
    ],
    customCheckbox: <RectRoundCheckbox4 />,
    showDefaultCircle: true,
    buttonName: i18n.t(`weightOnEvents.buttonName`),
    policyBlock: true,
  },
  [PageName.height]: {
    title: i18n.t('height.title'),
    nextUrl: getPath(PageName.weight),
    pageNumber: 24,
    progress: ((24 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    buttonName: i18n.t(`height.buttonName`),
    policyBlock: true,
    isShowError: false,
    textBlocks: [
      {
        className: 'height-description',
        iconNode: downloadImage(womanTypingHand),
        text: i18n.t(`height.textBlocks.text`),
        underIconText: i18n.t(`height.textBlocks.underIconText`),
      },
    ],
  },
  [PageName.weight]: {
    title: i18n.t('weight.title'),
    nextUrl: getPath(PageName.weightGoal),
    pageNumber: 25,
    progress: ((25 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    buttonName: i18n.t(`weight.buttonName`),
    policyBlock: true,
    isShowDescription: false,
    validateBMIRefValue: false,
    textBlocksF: (value: string, measurementSystem: string) => {
      const bmiValue = bmi(value, measurementSystem)
      const bmiCategory = bmiValue > 25 ? 'overweight' : bmiValue < 18.5 ? 'underweight' : 'normal'

      const bmiCategoryTranslated = i18n.t(`weight.type.${bmiCategory}`)

      const bmiNoteTemplate = i18n.t('weight.bmiNote')
      const bmiNote = bmiNoteTemplate
        .replace('{bmi}', String(bmiValue))
        .replace('{classification}', bmiCategoryTranslated)

      const description = i18n.t(`weight.description.${bmiCategory}`)

      const bmiIcons = {
        overweight: downloadImage(doubleExclamationMark),
        underweight: downloadImage(womanTypingHand),
        normal: downloadImage(sun),
      }

      return [
        {
          className: `weight-description ${bmiCategory}`,
          iconNode: bmiIcons[bmiCategory],
          text: bmiNote,
          underIconText: description,
        },
      ]
    },
  },

  [PageName.weightGoal]: {
    title: i18n.t('weightGoal.title'),
    nextUrl: getPath(PageName.age2),
    pageNumber: 26,
    progress: ((26 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    buttonName: i18n.t('weightGoal.buttonName'),
    policyBlock: true,
    isShowDescription: false,
    textBlocksF: (value: string) => {
      const weightData = JSON.parse(getItem(PageName.weight) || '')
      const weightValue = parseInt(weightData?.value)
      const measurementSystem = weightData?.measurementSystem
      return [
        {
          className: 'weight-goal-description',
          iconNode: downloadImage(womanTypingHand),
          text: i18n.t('weightGoal.text'),
          underIconText: i18n.t('weightGoal.underIconText'),
        },
        {
          className: 'weight-goal-limit',
          iconNode: downloadImage(smile),
          text: () => {
            if (parseInt(value) < weightValue) return ''
            // get error message
            return getTranslation('weightGoal.errorMessage').replace(
              '@{weight}',
              `${weightValue} ${measurementSystem}`
            )
          },
        },
      ]
    },
  },
  [PageName.age2]: {
    title: i18n.t('age2.title'),
    nextUrl: getPath(PageName.name),
    unit: i18n.t('age2.unit'),
    pageNumber: 27,
    progress: ((27 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    isEndangered: true,
    checkInputValue: (value: string) => !isNaN(+value),
    textBlocks: [
      {
        className: 'age-description',
        iconNode: downloadImage(womanTypingHand),
        text: i18n.t('age2.text'),
        underIconText: i18n.t('age2.underIconText'),
      },
    ],
    buttonName: i18n.t('age2.buttonName'),
    policyBlock: true,
  },
  [PageName.name]: {
    title: i18n.t('name.title'),
    nextUrl: getPath(PageName.profile),
    pageNumber: 28,
    progress: ((28 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    checkInputValue: () => true,
    onSubmit: (value: string) => {
      if (value) {
        setItem(contactNameKey, value)
      }
    },
    buttonName: i18n.t('name.buttonName'),
    policyBlock: true,
  },
  [PageName.event]: {
    title: i18n.t('event.title'),
    pageDescription: i18n.t('event.pageDescription'),
    nextUrl: getPath(PageName.eventDate),
    pageNumber: 29,
    progress: ((29 / PAGE_TOTAL_COUNT) * 100).toFixed(0),
    options: [
      { title: i18n.t('event.options.0.title'), image: downloadImage(sun) },
      { title: i18n.t('event.options.1.title'), image: downloadImage(bride) },
      { title: i18n.t('event.options.2.title'), image: downloadImage(island) },
      { title: i18n.t('event.options.3.title'), image: downloadImage(sportCup) },
      { title: i18n.t('event.options.4.title'), image: downloadImage(firecracker) },
      { title: i18n.t('event.options.5.title'), image: downloadImage(cake) },
      { title: i18n.t('event.options.6.title'), image: downloadImage(sunglassesFace) },
      {
        title: i18n.t('event.options.7.title'),
        image: downloadImage(womanCrossArms),
        nextUrl: '/preparing-plan',
      },
    ],
    policyBlock: true,
  },
  [PageName.eventDate]: {
    title: i18n.t('eventDate.title'),
    pageDescription: i18n.t('eventDate.pageDescription'),
    placeholderText: i18n.t('eventDate.placeholderText'),
    nextUrl: getPath(PageName.weightLossProgram),
    skipQuestionUrl: getPath(PageName.preparingPlan),
    calendarIconNode: <CalendarSquare />,
    buttonName: i18n.t('eventDate.buttonName'),
    skipButtonName: i18n.t('eventDate.skipButtonName'),
    textBlocks: [
      {
        className: 'event-date-description',
        iconNode: <WomanCrosshanded />,
        text: i18n.t('eventDate.misc'),
      },
    ],
    policyBlock: true,
  },
}
